import { useEffect, useState } from "react";
import Header from "./header/Header";

export default function ContactSupportPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [details, setDetails] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setName(sessionStorage.getItem("supportName") || "");
    setScreenshot(sessionStorage.getItem("supportScreenshot"));
  }, []);

  const base64ToFile = (base64, filename) => {
    if (!base64) return null;
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  };

  const validate = () => {
    const newErrors = {};

    if (!name.trim()) newErrors.name = "Name is required";

    if (!email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Invalid email";

    if (!phone.trim()) newErrors.phone = "Phone is required";
    else if (!/^[0-9]{12}$/.test(phone))
      newErrors.phone = "Enter valid 12 digit phone";

    if (!details.trim()) newErrors.details = "Details are required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ open: true, message, type });
    setTimeout(() => setSnackbar({ open: false, message: "", type }), 4000);
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("company_id", sessionStorage.getItem("companyId") || "");
      formData.append(
        "interview_id",
        sessionStorage.getItem("interviewId") || "",
      );
      formData.append("candidate_comment", details);
      formData.append("name", name);

      const file = base64ToFile(screenshot, "screenshot.png");
      if (file) formData.append("files", file);

      const token = sessionStorage.getItem("interviewAuthToken");

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL_NEW}/support_team_candidate/send-email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      if (!res.ok) throw new Error("API failed");

      showSnackbar("Support request submitted successfully", "success");
    } catch (e) {
      console.error(e);
      showSnackbar("Failed to submit support request", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="mt-4 ml-4">
        <Header />
      </div>
      <div className="flex justify-center items-start pt-10">
        <div className="bg-white shadow-lg rounded-lg w-[520px] p-6">
          <h1 className="text-xl font-semibold mb-4">
            Contact Support / Help Desk
          </h1>

          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                value={name}
                disabled
                className="w-full border rounded px-2 py-1 bg-gray-100"
              />
              {errors.name && (
                <p className="text-red-500 text-xs">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="text-xs text-gray-500">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border rounded px-2 py-1"
              />
              {errors.email && (
                <p className="text-red-500 text-xs">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="text-xs text-gray-500">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full border rounded px-2 py-1"
              />
              {errors.phone && (
                <p className="text-red-500 text-xs">{errors.phone}</p>
              )}
            </div>

            <div>
              <label className="text-xs text-gray-500">
                Details <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={4}
                maxLength={1000}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                className="w-full border rounded px-2 py-1"
              />
              <div className="text-[11px] text-gray-400 text-right">
                {details.length}/1000
              </div>
              {errors.details && (
                <p className="text-red-500 text-xs">{errors.details}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button
              onClick={() => window.close()}
              className="px-3 py-1 rounded border"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center justify-center px-6 py-1.5 btn-size transition-colors duration-200 bg-btnBgColor text-white disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Submitting...
                </span>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </div>

        {snackbar.open && (
          <div
            className={`fixed bottom-5 left-1/2 -translate-x-1/2 px-4 py-2 rounded shadow-lg text-white ${
              snackbar.type === "error" ? "bg-red-600" : "bg-green-600"
            }`}
          >
            {snackbar.message}
          </div>
        )}
      </div>
    </div>
  );
}
